// src/utils/formatting.js

/**
 * Format a number as currency (USD)
 * @param {number} value - The value to format
 * @param {boolean} includeSymbol - Whether to include the $ symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, includeSymbol = true) => {
    if (value === null || value === undefined) return '';

    const formatter = new Intl.NumberFormat('en-US', {
        style: includeSymbol ? 'currency' : 'decimal',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return formatter.format(value);
};

/**
 * Format a date string to a human-readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "Mar 15, 2025")
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

/**
 * Format a month and year (e.g., "Mar 2025")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted month and year
 */
export const formatMonthYear = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Calculate months between two dates
 * @param {Date|string} startDate - Start date or ISO string
 * @param {Date|string} endDate - End date or ISO string 
 * @returns {number} Number of months between dates
 */
export const monthsBetween = (startDate, endDate) => {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);

    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
};

/**
 * Format a percentage value
 * @param {number} value - Percentage value (e.g., 85.4)
 * @returns {string} Formatted percentage (e.g., "85%")
 */
export const formatPercentage = (value) => {
    if (value === null || value === undefined) return '';
    return `${Math.round(value)}%`;
};